import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';

import { Council, Society, SocietyDirector } from 'types';
import { DATE_FORMAT_WITH_TIME_ALT } from 'constants/defaultConstants';

import Button from 'modules/_shared/components/Buttons';
import councilStatus from 'modules/administratives/constants/councilStatus';
import CouncilBadges from 'modules/administratives/components/CouncilBadges';

import { getDirectorDetailsUrl } from '../utils/getDirectorDetailsUrl';
import { getCouncilStatusData } from '../../CouncilDetails/utils/getCouncilStatusData';
import { CouncilMemberAvatar } from '../components/CouncilMemberAvatar';

type UseCouncilBasicInfoCardProps = {
  council: Council;
  society: Society;
  isAdmin: boolean;
};

export const useCouncilBasicInfoCard = ({
  council,
  society,
  isAdmin,
}: UseCouncilBasicInfoCardProps) => {
  const { t } = useTranslate();

  const { statusLabel, statusBadge, statusValue } =
    getCouncilStatusData(council);

  const presidentData = society?.directors?.find(
    (director) => director._id === council?.president
  ) as SocietyDirector;

  const secretaryData = society?.directors?.find(
    (director) => director._id === council?.secretary
  ) as SocietyDirector;

  const items = [
    {
      title: t('Name'),
      value: council?.name || '-',
    },
    {
      title: t('Date'),
      value: council?.date
        ? format(new Date(council.date), DATE_FORMAT_WITH_TIME_ALT)
        : '-',
    },
    {
      title: t('Status'),
      value: CouncilBadges.Status && (
        <CouncilBadges.Status
          t={t}
          style={statusBadge}
          text={statusLabel}
          status={statusValue as keyof typeof councilStatus}
        />
      ),
    },
    {
      title: t('Place'),
      value: council?.place || '-',
    },
    {
      title: t('Link'),
      value: (
        <Button
          className="btn-sm text-white"
          onClick={() => window.open(council?.onlineAccess, '_blank')}
        >
          <em className="icon ni ni-external mr-1 text-white" />
          {t('VideoCall')}
        </Button>
      ),
    },
    {
      title: t('President'),
      value: isAdmin ? (
        <Link to={getDirectorDetailsUrl(presidentData, society)}>
          <CouncilMemberAvatar director={presidentData} />
        </Link>
      ) : (
        <CouncilMemberAvatar director={presidentData} />
      ),
    },
    {
      title: t('Secretary'),
      value: isAdmin ? (
        <Link to={getDirectorDetailsUrl(secretaryData, society)}>
          <CouncilMemberAvatar director={secretaryData} />
        </Link>
      ) : (
        <CouncilMemberAvatar director={secretaryData} />
      ),
    },
    {
      title: t('LastMod.'),
      value: council?.lastModifiedDate
        ? format(new Date(council.lastModifiedDate), DATE_FORMAT_WITH_TIME_ALT)
        : '-',
    },
  ];

  return {
    data: items,
  };
};
